class CollapsibleHeader extends Collapsible {
  constructor() {
    super();
    this.header = this.closest('[data-header]');
    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');

      // extra events
      this.shadow?.addEventListener('click', this.closeAll.bind(this));

      // on key up
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    let group = this.querySelector(`[data-collapsible-group].${toggleClass}`);
    if (group) {
      this.close(group);
    }
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');
      this.inverse = !!this.header.classList.contains('header--inverse');

      // Extra events
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });

      this.shadow?.addEventListener('click', (event) => {
        this.remove(event);
      });

      this.addEventListener('click', () => {
        this.setTopMargin();
      });
    }
  }

  setTopMargin() {
    const topMargin = this.getTopHeight();
    this.menuDrawerContent = document.querySelector('nav.menu-drawer__content');
    this.menuDrawerContent.style.marginTop = `${topMargin}px`;
  }

  getTopHeight() {
    const rect = this.header.getBoundingClientRect();
    return rect.top;
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('menu-drawer-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });

    // Remove classes in case the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.body.classList.remove('header-is-open');
      document.body.classList.remove('menu-drawer-is-open');
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-search-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      document.body.classList.add('header-search-is-open');
    }
    if (this.target) {
      trapFocus(this.target);
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      document.body.classList.remove('header-search-is-open');
    }
    removeTrapFocus();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select element
    this.headerElement = this;
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('.shopify-section-group-header-group');
    this.announcementBar = document.querySelector('.announcement-bar');
    this.announcementTimer = document.querySelector('.announcement-timer');

    this.headerInverse = !!this.headerElement.classList.contains('header--inverse');
    this.mobileNav = this.querySelector('toggle-mobile-navigation');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('toggle-search');
    this.productInfo = document.querySelector('[data-product-info]');
    this.headerNav = this.querySelector('collapsible-header');
    const headerNavOptions = JSON.parse(this.headerNav.dataset.options);
    this.hoverDelay = headerNavOptions.hoverDelay;

    // Get page type
    this.pageType = Shopify.pageType;

    if (this.headerWrapper) {
      // scroll parameters
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;

      if (this.announcementBar) {
        const announcementBarHeight = this.announcementBar.offsetHeight;
        this.scrollOffset += announcementBarHeight;
        this.headerStart += announcementBarHeight;
      }

      if (this.headerElement) {
        this.scrollOffset += this.headerElement.offsetHeight;
      }

      // scroll event
      this.raf = null; // Safe request animation frame
      (this.timer = null), // Safe scroll timer
        window.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.timer);
            if (!this.raf) {
              // A request animation frame is used to animate on max 60fps.
              this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
            }
            this.timer = setTimeout(() => {
              // Stop animation, cancel request animation frame
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }, 50);
          },
          false
        );
    }

    // Reduce actions (So the event can also be removed)
    this.reducer = {
      mouseEnterHeader: (e) => this.debouncedOnMouse(e, 'mouseEnterHeader'),
      mouseLeaveHeader: (e) => this.debouncedOnMouse(e, 'mouseLeaveHeader'),
    };

    this.addEventListener('mouseenter', this.reducer.mouseEnterHeader);
    this.addEventListener('mouseleave', this.reducer.mouseLeaveHeader);
  }

  /*
    Debounced mouse event
    @param e {object}: mouseevent
    @param type {string}: open/close/closeAll
    Called by add event listener -> reducer.
  */
  debouncedOnMouse(e, type) {
    if (!e) {
      return false;
    }

    // clear old trigger
    clearTimeout(this.timeout);

    // add new trigger
    this.timeout = setTimeout(() => {
      switch (type) {
        case 'mouseEnterHeader':
          this.toggleHeaderClass(true);
          break;
        case 'mouseLeaveHeader':
          this.toggleHeaderClass(false);
          break;
        default:
          break;
      }
    }, this.hoverDelay);
  }

  toggleHeaderClass(open = true) {
    if (!this.header) {
      return false;
    }
    //only on desktop
    if (window.innerWidth > 768) {
      if (open) {
        // set header open
        document.body.classList.add('header-is-open');
        this.inverse && this.header.classList.add('header-inverse-solid');
      } else {
        // set header closed if all menu items are closed
        document.body.classList.remove('header-is-open');
        this.inverse && this.header.classList.remove('header-inverse-solid');
      }
    }
  }

  scrollAnimation() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    // only animate if header is closed.
    if (!document.body.classList.contains('header-is-open')) {
      if (scrollTop + window.innerHeight > document.body.clientHeight) {
        this.hide();
      } else if (scrollTop > this.currentScrollTop && scrollTop > this.scrollOffset) {
        this.hide();
      } else if (scrollTop <= this.headerStart) {
        this.revealTop();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerStart) {
        this.reveal();
      }
    }

    this.currentScrollTop = scrollTop;

    // Recall request animation loop
    this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
  }

  hide() {
    if (this.headerElement.classList.contains('header--sticky')) {
      document.body.classList.add('header-sticky');
    } else {
      document.body.classList.add('header-hidden', 'header-sticky');
    }

    this.closeHeaderNav();
    this.closeMobileNav();
    this.closeSearch();
    this.productInfoPosition('up');
  }

  reveal() {
    document.body.classList.remove('header-hidden');
    if (this.headerInverse) {
      document.body.classList.add('header-inverse-solid');
    }
    this.productInfoPosition('down');
  }

  revealTop() {
    if (this.headerInverse && !document.body.classList.contains('header-is-open')) {
      document.body.classList.remove('header-inverse-solid');
    }
    if (this.headerElement.classList.contains('header--sticky')) {
      document.body.classList.remove('header-sticky');
    } else {
      document.body.classList.remove('header-hidden', 'header-sticky');
    }
    this.productInfoPosition('up');
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 768) {
      return false;
    }
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if (!this.mobileNav) {
      return false;
    }
    this.mobileNav.remove();
  }

  closeSearch() {
    if (!this.search || !this.searchToggle) {
      return false;
    }
    if (this.search.classList.contains('header__search--is-active')) {
      this.searchToggle.remove();
    }
  }

  productInfoPosition(pos = 'up') {
    if (!this.productInfo) {
      return;
    }
    if (pos == 'down') {
      this.productInfo.classList.replace('top-0', 'top-[4.5rem]');
    } else {
      this.productInfo.classList.replace('top-[4.5rem]', 'top-0');
    }
  }
}

if (!customElements.get('collapsible-header')) {
  customElements.define('collapsible-header', CollapsibleHeader);
}

if (!customElements.get('toggle-mobile-navigation')) {
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

if (!customElements.get('toggle-search')) {
  customElements.define('toggle-search', ToggleSearch);
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
